import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  BarChart3,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Pin,
  PinOff,
  Lock,
  Clock
} from 'lucide-react'
import { pollsAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import PageHeader from '@/components/PageHeader'
export default function PollsPage() {
  const { user: currentUser, can } = useAuth()
  const [polls, setPolls] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [pollFormData, setPollFormData] = useState({
    question: '',
    description: '',
    options: ['', ''],
    showVoters: false,
  })

  useEffect(() => {
    fetchPolls()
  }, [])

  const fetchPolls = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await pollsAPI.getPolls()
      setPolls(response.data.polls || [])
    } catch (err) {
      console.error('Failed to fetch polls:', err)
      setError('Failed to load polls')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePoll = async () => {
    try {
      // Validation
      if (!pollFormData.question.trim()) {
        alert('Question is required')
        return
      }

      const validOptions = pollFormData.options.filter(opt => opt.trim() !== '')
      if (validOptions.length < 2) {
        alert('At least 2 options are required')
        return
      }

      await pollsAPI.createPoll({
        question: pollFormData.question,
        description: pollFormData.description,
        options: validOptions,
        showVoters: pollFormData.showVoters,
      })

      setCreateDialogOpen(false)
      setPollFormData({
        question: '',
        description: '',
        options: ['', ''],
        showVoters: false,
      })
      fetchPolls()
    } catch (err) {
      console.error('Failed to create poll:', err)
      alert(err.response?.data?.error || 'Failed to create poll')
    }
  }

  const handleVote = async (pollId, optionId) => {
    try {
      await pollsAPI.votePoll(pollId, optionId)
      fetchPolls() // Refresh to show updated results
    } catch (err) {
      console.error('Failed to vote:', err)
      alert(err.response?.data?.error || 'Failed to vote')
    }
  }

  const handleDeletePoll = async (pollId) => {
    if (!confirm('Are you sure you want to delete this poll?')) return

    try {
      await pollsAPI.deletePoll(pollId)
      fetchPolls()
    } catch (err) {
      console.error('Failed to delete poll:', err)
      alert(err.response?.data?.error || 'Failed to delete poll')
    }
  }

  const handleClosePoll = async (pollId) => {
    if (!confirm('Close this poll? No more votes will be accepted.')) return

    try {
      await pollsAPI.closePoll(pollId)
      fetchPolls()
    } catch (err) {
      console.error('Failed to close poll:', err)
      alert(err.response?.data?.error || 'Failed to close poll')
    }
  }

  const handleTogglePin = async (pollId) => {
    try {
      await pollsAPI.togglePinPoll(pollId)
      fetchPolls()
    } catch (err) {
      console.error('Failed to toggle pin:', err)
      alert(err.response?.data?.error || 'Failed to toggle pin')
    }
  }

  const addOption = () => {
    if (pollFormData.options.length < 10) {
      setPollFormData({
        ...pollFormData,
        options: [...pollFormData.options, '']
      })
    }
  }

  const removeOption = (index) => {
    if (pollFormData.options.length > 2) {
      const newOptions = pollFormData.options.filter((_, i) => i !== index)
      setPollFormData({ ...pollFormData, options: newOptions })
    }
  }

  const updateOption = (index, value) => {
    const newOptions = [...pollFormData.options]
    newOptions[index] = value
    setPollFormData({ ...pollFormData, options: newOptions })
  }

  const getPercentage = (voteCount, totalVotes) => {
    if (totalVotes === 0) return 0
    return Math.round((voteCount / totalVotes) * 100)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green mx-auto mb-4" />
          <p className="text-muted-foreground">Loading polls...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader icon={BarChart3} title="Polls & Voting" />
          <p className="text-muted-foreground mt-1">
            Vote on community polls and create your own
          </p>
        </div>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="flex items-center space-x-2 bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
          variant="outline"
        >
          <Plus className="h-4 w-4" />
          <span>Create Poll</span>
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Polls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {polls.length === 0 ? (
          <Card className="col-span-full border-neon-green/30">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BarChart3 className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <p className="text-lg text-muted-foreground">No polls yet</p>
              <p className="text-sm text-muted-foreground/70">Be the first to create one!</p>
            </CardContent>
          </Card>
        ) : (
          polls.map((poll) => (
            <Card
              key={poll.id}
              className={cn(
                'border-neon-green/30 transition-all',
                poll.is_pinned && 'border-neon-purple/50 shadow-neon-purple'
              )}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {poll.is_pinned && (
                        <Badge variant="outline" className="text-neon-purple border-neon-purple/30">
                          <Pin className="h-3 w-3 mr-1" />
                          Pinned
                        </Badge>
                      )}
                      {!poll.is_active && (
                        <Badge variant="outline" className="text-red-500 border-red-500/30">
                          <Lock className="h-3 w-3 mr-1" />
                          Closed
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg">{poll.question}</CardTitle>
                    {poll.description && (
                      <CardDescription className="mt-1">{poll.description}</CardDescription>
                    )}
                  </div>
                  {(can('manage_polls') || currentUser?.id === poll.created_by) && (
                    <div className="flex items-center space-x-1">
                      {can('manage_polls') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleTogglePin(poll.id)}
                          className="h-7 w-7 p-0"
                        >
                          {poll.is_pinned ? (
                            <PinOff className="h-3 w-3" />
                          ) : (
                            <Pin className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      {poll.is_active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleClosePoll(poll.id)}
                          className="h-7 w-7 p-0 text-orange-500"
                        >
                          <Lock className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePoll(poll.id)}
                        className="h-7 w-7 p-0 text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                  <span>By {poll.creator_display_name || poll.creator_username}</span>
                  <span>{poll.total_votes} votes</span>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {poll.options.map((option) => {
                  const percentage = getPercentage(option.vote_count, poll.total_votes)
                  const isUserVote = poll.user_vote === option.id

                  return (
                    <button
                      key={option.id}
                      onClick={() => poll.is_active && handleVote(poll.id, option.id)}
                      disabled={!poll.is_active}
                      className={cn(
                        'w-full text-left relative overflow-hidden rounded-md border p-3 transition-all',
                        poll.is_active
                          ? 'hover:border-neon-green/50 cursor-pointer'
                          : 'cursor-not-allowed opacity-70',
                        isUserVote && 'border-neon-green/70 bg-neon-green/5'
                      )}
                    >
                      {/* Progress bar background */}
                      <div
                        className={cn(
                          'absolute inset-0 transition-all',
                          isUserVote ? 'bg-neon-green/10' : 'bg-muted/50'
                        )}
                        style={{ width: `${percentage}%` }}
                      />

                      {/* Option content */}
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {isUserVote && (
                            <CheckCircle className="h-4 w-4 text-neon-green" />
                          )}
                          <span className="font-medium">{option.option_text}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-muted-foreground">
                            {option.vote_count} votes
                          </span>
                          <span className="text-sm font-medium">{percentage}%</span>
                        </div>
                      </div>

                      {/* Show voters if enabled */}
                      {poll.show_voters && option.voters && option.voters.length > 0 && (
                        <div className="relative mt-2 text-xs text-muted-foreground">
                          {option.voters.map((voter, i) => (
                            <span key={i}>
                              {voter.display_name || voter.username}
                              {i < option.voters.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  )
                })}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Poll Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="border-neon-green/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Poll</DialogTitle>
            <DialogDescription>
              Ask a question and add options for people to vote on
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={pollFormData.question}
                onChange={(e) => setPollFormData({ ...pollFormData, question: e.target.value })}
                placeholder="e.g., Which game should we play next?"
                className="border-neon-green/30"
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={pollFormData.description}
                onChange={(e) => setPollFormData({ ...pollFormData, description: e.target.value })}
                placeholder="Add more context to your poll..."
                className="border-neon-green/30"
                maxLength={500}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Options * (min 2, max 10)</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addOption}
                  disabled={pollFormData.options.length >= 10}
                  className="text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Option
                </Button>
              </div>
              <div className="space-y-2">
                {pollFormData.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="border-neon-green/30"
                      maxLength={100}
                    />
                    {pollFormData.options.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeOption(index)}
                        className="h-9 w-9 p-0 text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-voters"
                checked={pollFormData.showVoters}
                onCheckedChange={(checked) =>
                  setPollFormData({ ...pollFormData, showVoters: checked })
                }
              />
              <Label htmlFor="show-voters" className="cursor-pointer">
                Show who voted for each option
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePoll}
              className="bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
              disabled={
                !pollFormData.question.trim() ||
                pollFormData.options.filter(opt => opt.trim()).length < 2
              }
            >
              Create Poll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
